import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { AutoCompleteModule } from 'primeng/autocomplete';
import { ChipsModule } from 'primeng/chips';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DataViewModule } from 'primeng/dataview';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ListboxModule } from 'primeng/listbox';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { StepsModule } from 'primeng/steps';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';

import { HisSharedModule } from 'hisng';

import { CommonSharedModule } from 'app/fs/res/common/common-shared.module';

import { JournalSharedModule } from './component/journal/journal-shared.module';
import { IdentifierSharedModule } from './component/identifier/identifier-shared.module';
import { PublisherSharedModule } from './component/publisher/publisher-shared.module';

import { PublicationSearchFormComponent } from './component/publication/search/publication-search-form.component';
import { PublicationSearchResultComponent } from './component/publication/search/publication-search-result.component';
import { PublicationImportSearchResultComponent } from './component/publication/import/publication-import-search-result.component';
import { PublicationEditFormComponent } from './component/publication/edit/publication-edit-form.component';
import { PublicationDataTabComponent } from './component/publication/edit/publication-data-tab.component';
import { PublicationClassificationTabComponent } from './component/publication/edit/publication-classification-tab.component';
import { PublicationResearchTabComponent } from './component/publication/edit/publication-research-tab.component';
import { PublicationViewDetailsComponent } from './component/publication/view/publication-view-details.component';
import { PublicationParentPlamComponent } from './component/publication/edit/basic-data/publication-parent-plam.component';
import { PublicationNominationPlamComponent } from './component/publication/edit/basic-data/publication-nomination-plam.component';
import { PublicationEditMainDataComponent } from './component/publication/edit/basic-data/publication-edit-main-data.component';
import { PublicationEditAdditionalDataComponent } from './component/publication/edit/basic-data/publication-edit-additional-data.component';
import { PublicationCreatorPanelComponent } from './component/publication/edit/basic-data/publication-creator-panel.component';
import {
    PublicationCreatorWizardComponent
} from './component/publication/edit/basic-data/creator-wizard/publication-creator-wizard.component';
import {
    PublicationCreatorWizardNameComponent
} from './component/publication/edit/basic-data/creator-wizard/publication-creator-wizard-name.component';
import {
    PublicationCreatorWizardPersonComponent
} from './component/publication/edit/basic-data/creator-wizard/publication-creator-wizard-person.component';
import {
    PublicationCreatorWizardOrganizationComponent
} from './component/publication/edit/basic-data/creator-wizard/publication-creator-wizard-organization.component';
import { PublicationImportFormComponent } from './component/publication/import/publication-import-form.component';
import { PublicationBibtexImportComponent } from './component/publication/import/publication-bibtex-import.component';
import { PublicationExcelImportComponent } from './component/publication/import/publication-excel-import.component';
import { PublicationOrcidImportComponent } from './component/publication/import/publication-orcid-import.component';
import { PublicationDuplicatesButtonComponent } from './component/publication/duplicates/publication-duplicates-button.component';
import { PublicationDuplicatesSearchFormComponent } from './component/publication/duplicates/publication-duplicates-search-form.component';
import { PublicationDuplicatesSearchResultComponent } from './component/publication/duplicates/publication-duplicates-search-result.component';
import { PublicationDuplicatesSearchResultListComponent }
    from './component/publication/duplicates/publication-duplicates-search-result-list.component';
import { PublicationViewDetailsVCardComponent } from './component/publication/view/publication-view-details-vcard.component';
import { PublicationAbstractTabComponent } from './component/publication/edit/publication-abstract-tab.component';
import { PublicationEditAbstractComponent } from './component/publication/edit/abstract/publication-edit-abstract.component';
import { PublicationCitationComponent } from './component/publication/view/publication-citation-dialog.component';
import { PublicationSearchResultListComponent } from './component/publication/search/publication-search-result-list.component';
import { PublicationSearchResultListElementComponent } from './component/publication/search/publication-search-result-list-element.component';
import { PublicationSearchOrcidUserFormComponent } from './component/publication/import/publication-search-orcid-user-form.component';
import { PublicationSearchOrcidUserResultComponent } from './component/publication/import/publication-search-orcid-user-result.component';
import { PublicationConfigurationComponent } from './component/publication/configuration/publication-configuration.component';

import { FundingProgramCreateDialogComponent } from './component/publication/edit/funding-program/funding-program-create-dialog.component';
import { FundingProgramPanelComponent } from './component/publication/edit/funding-program/funding-program-panel.component';
import { PublicationChangeRequestFormComponent } from './component/publication/change-request/publication-change-request-form';
import { PublicationDetailsPanelComponent } from './component/publication/change-request/publication-details-panel.component';
import { PublicationImportSearchFormComponent } from './component/publication/import/publication-import-search-form.component';
import { CalendarModule } from 'primeng/calendar';
import { PublicationEditAccessDataComponent } from './component/publication/edit/basic-data/publication-edit-access-data.component';
import { PublicationDocumentsTabComponent } from './component/publication/edit/publication-documents-tab.component';
import { PublicationEditDocumentsDataComponent } from './component/publication/edit/basic-data/publication-edit-documents-data.component';
import { PublicationEditContactDataComponent } from './component/publication/edit/basic-data/publication-edit-contact-data.component';
import { PublicationContactDialogComponent } from './component/publication/edit/basic-data/publication-contact-dialog.component';
import { PublicationDocumentDialogComponent } from './component/publication/edit/basic-data/publication-document-dialog.component';

/**
 * SharedModule for publications.
 */
@NgModule( {
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,

        // primeng
        AutoCompleteModule,
        ChipsModule,
        ConfirmDialogModule,
        DataViewModule,
        DialogModule,
        FileUploadModule,
        InputTextModule,
        InputTextareaModule,
        ListboxModule,
        PaginatorModule,
        PanelModule,
        StepsModule,
        TabViewModule,
        TableModule,
        CalendarModule,

        // HIS
        HisSharedModule,
        CommonSharedModule,

        // RES
        PublisherSharedModule,
        JournalSharedModule,
        IdentifierSharedModule
    ],

    declarations: [
        PublicationSearchFormComponent,
        PublicationSearchResultComponent,
        PublicationImportSearchResultComponent,
        PublicationEditFormComponent,
        PublicationDataTabComponent,
        PublicationEditMainDataComponent,
        PublicationEditAdditionalDataComponent,
        PublicationEditAccessDataComponent,
        PublicationCreatorPanelComponent,
        PublicationParentPlamComponent,
        PublicationNominationPlamComponent,
        PublicationAbstractTabComponent,
        PublicationEditAbstractComponent,
        PublicationClassificationTabComponent,
        PublicationResearchTabComponent,
        PublicationViewDetailsComponent,
        PublicationViewDetailsVCardComponent,
        PublicationImportFormComponent,
        PublicationBibtexImportComponent,
        PublicationExcelImportComponent,
        PublicationOrcidImportComponent,
        PublicationImportSearchFormComponent,
        PublicationSearchOrcidUserFormComponent,
        PublicationSearchOrcidUserResultComponent,
        PublicationDuplicatesButtonComponent,
        PublicationDuplicatesSearchFormComponent,
        PublicationDuplicatesSearchResultComponent,
        PublicationDuplicatesSearchResultListComponent,
        PublicationCitationComponent,
        PublicationSearchResultListComponent,
        PublicationSearchResultListElementComponent,
        PublicationConfigurationComponent,
        PublicationChangeRequestFormComponent,
        PublicationDetailsPanelComponent,
        PublicationDocumentsTabComponent,
        PublicationEditDocumentsDataComponent,
        PublicationEditContactDataComponent,
        PublicationContactDialogComponent,
        PublicationDocumentDialogComponent,
        FundingProgramPanelComponent,
        FundingProgramCreateDialogComponent,

        // Wizard for creators
        PublicationCreatorWizardComponent,
        PublicationCreatorWizardNameComponent,
        PublicationCreatorWizardPersonComponent,
        PublicationCreatorWizardOrganizationComponent,
    ],

    exports: []

    // NO PROVIDERS! Providers need to go into PublicationCoreModule
} )
export class PublicationSharedModule {
}
